import {
  useCallback, useDeferredValue, useEffect, useMemo, useState,
} from 'react';
import { useSelector } from 'react-redux';

import featureFlags from 'constants/featureFlags';
import { selectUserId, selectUserIsAdmin } from 'redux/reducers/authReducer';
import {
  ALL_KINDS,
  ExplorerFile,
  ExplorerFolder,
  SearchScope,
  SortDir,
  SortKey,
  buildExplorerTree,
  decodeFolderParam,
  findFolder,
  flattenTree,
  selectVisibleEntries,
} from 'helper/products/fileExplorer';
import {
  companyScopeKey,
  shouldSkipFileList,
  toFileListArgs,
} from 'helper/products/fileViewerCompany';
import useFileList from './useFileList';
import useFileViewerCompany from './useFileViewerCompany';
import useFolderPath from './useFolderPath';
import useFileViewerFavorites from './useFileViewerFavorites';
import useFileViewerDownload from './useFileViewerDownload';
import useFileViewerTransfers from './useFileViewerTransfers';

/** Linhas renderizadas por vez: a busca global pode casar milhares de arquivos. */
export const PAGE_SIZE = 200;

/** Ordem inicial ao trocar de coluna: nome A–Z; data e tamanho, maiores primeiro. */
const DEFAULT_DIR: Record<SortKey, SortDir> = { name: 1, size: -1, date: -1 };
const ROOT_PATH: string[] = [];

const ancestorKeys = (path: string[]) => path.map((_, i) => path.slice(0, i + 1).join('/'));

/**
 * Estado e ações da tela "Meus Arquivos" (desktop e mobile compartilham tudo; só a apresentação
 * muda). A pasta aberta e a empresa (admin, atrás da flag `meusArquivosEmpresa`) vivem na URL;
 * busca, filtro, ordenação e seleção vivem aqui.
 */
export default function useFileExplorer() {
  const isAdmin: boolean = useSelector(selectUserIsAdmin);
  const userId: number = useSelector(selectUserId);
  // Admin com o seletor de empresa: a empresa escolhida é a lista pedida. Sem a flag (ou para
  // cliente), o escopo é "todas" e a lista é a de hoje, sem parâmetro.
  const canChooseCompany = isAdmin && featureFlags.meusArquivosEmpresa;
  const { scope: company, setCompany, needsCompany } = useFileViewerCompany({
    isAdmin: canChooseCompany,
    userId,
  });
  const companyKey = companyScopeKey(company);
  const {
    data, isLoading, isRefreshing, refresh, updatedAt,
  } = useFileList({
    args: toFileListArgs(company, canChooseCompany),
    skip: shouldSkipFileList(company, canChooseCompany),
  });
  const { path, openFolder } = useFolderPath();
  const folderKey = path.join('/');

  const root = useMemo(() => buildExplorerTree(data ?? []), [data]);
  const currentFolder = findFolder(root, path);
  const fileIndex = useMemo(() => new Map(
    flattenTree(root)
      .filter((node): node is ExplorerFile => node.kind === 'file')
      .map((file) => [file.key, file]),
  ), [root]);

  const [query, setQuery] = useState('');
  const [scope, setScope] = useState<SearchScope>('here');
  const [kind, setKind] = useState<string>(ALL_KINDS);
  // Mais recentes no topo (Marcelo, 23/10/2026): é o que se procura numa pasta de relatórios
  // diários.
  const [sort, setSort] = useState<{ key: SortKey, dir: SortDir }>({ key: 'date', dir: -1 });
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const [toast, setToast] = useState('');

  // Link para pasta que não existe mais (ou foi renomeada): abre o ancestral mais próximo.
  useEffect(() => {
    if (!data || currentFolder) return;
    let existing = path.slice(0, -1);
    while (existing.length && !findFolder(root, existing)) existing = existing.slice(0, -1);
    openFolder(existing, { replace: true });
  }, [data, currentFolder, path, root, openFolder]);

  // Trocou de empresa (seletor, link ou "voltar" do navegador): busca, filtro, seleção, "Mostrar
  // mais" e pastas abertas eram da árvore anterior. A pasta já voltou à raiz com a URL. Fica antes
  // do efeito da pasta: na mesma passada, o dele reabre os ancestrais da pasta do link.
  useEffect(() => {
    setQuery('');
    setKind(ALL_KINDS);
    setSelected(new Set());
    setLimit(PAGE_SIZE);
    setExpanded(new Set());
  }, [companyKey]);

  // Trocou de pasta (clique, breadcrumb, árvore ou "voltar" do navegador).
  useEffect(() => {
    setSelected(new Set());
    setLimit(PAGE_SIZE);
    setKind(ALL_KINDS);
    setExpanded((current) => {
      const next = new Set(current);
      ancestorKeys(decodeFolderParam(folderKey)).forEach((key) => next.add(key));
      return next;
    });
  }, [folderKey]);

  const goTo = useCallback((next: string[]) => {
    // Resultado da busca global abre a pasta "limpa"; a busca local continua valendo.
    if (scope === 'all') setQuery('');
    openFolder(next);
  }, [openFolder, scope]);

  const goUp = useCallback(() => goTo(path.slice(0, -1)), [goTo, path]);

  const effectivePath = currentFolder ? path : ROOT_PATH;
  // A digitação nunca espera o filtro: em árvores grandes a lista acompanha logo depois.
  const deferredQuery = useDeferredValue(query);
  const visible = useMemo(() => {
    const params = {
      root, path: effectivePath, query: deferredQuery, scope, sortKey: sort.key, sortDir: sort.dir,
    };
    const result = selectVisibleEntries({ ...params, kind });
    // O tipo escolhido sumiu do resultado (nova busca): volta para "Todos".
    return result.kinds.some((k) => k.kind === kind)
      ? result
      : selectVisibleEntries({ ...params, kind: ALL_KINDS });
  }, [root, effectivePath, deferredQuery, scope, sort.key, sort.dir, kind]);
  const activeKind = visible.kinds.some((k) => k.kind === kind) ? kind : ALL_KINDS;

  const sortBy = useCallback((key: SortKey) => {
    setSort((current) => ({
      key,
      dir: current.key === key ? (-current.dir as SortDir) : DEFAULT_DIR[key],
    }));
  }, []);
  const setSortKey = useCallback((key: SortKey) => setSort({ key, dir: DEFAULT_DIR[key] }), []);

  const changeQuery = useCallback((value: string) => {
    setQuery(value);
    setLimit(PAGE_SIZE);
  }, []);

  // ── Seleção (só arquivos; chave estável para sobreviver ao refresh) ──
  const visibleFiles = useMemo(
    () => visible.entries.filter((node): node is ExplorerFile => node.kind === 'file'),
    [visible.entries],
  );
  const selectedFiles = useMemo(
    () => Array.from(selected)
      .map((key) => fileIndex.get(key))
      .filter((f): f is ExplorerFile => !!f),
    [selected, fileIndex],
  );
  const allVisibleSelected = visibleFiles.length > 0
    && visibleFiles.every((f) => selected.has(f.key));
  const someVisibleSelected = visibleFiles.some((f) => selected.has(f.key));

  const toggleSelected = useCallback((key: string) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const toggleAllVisible = useCallback(() => {
    setSelected((current) => {
      const next = new Set(current);
      if (allVisibleSelected) visibleFiles.forEach((f) => next.delete(f.key));
      else visibleFiles.forEach((f) => next.add(f.key));
      return next;
    });
  }, [allVisibleSelected, visibleFiles]);

  const clearSelection = useCallback(() => setSelected(new Set()), []);

  // ── Árvore lateral ──
  const toggleExpanded = useCallback((key: string) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  // ── Favoritos ──
  const { favorites, isFavorite, toggleFavorite } = useFileViewerFavorites(userId);
  const favoriteFolders = useMemo(
    () => favorites
      .map((key) => findFolder(root, decodeFolderParam(key)))
      .filter((folder): folder is ExplorerFolder => !!folder && folder.path.length > 0),
    [favorites, root],
  );
  const togglePin = useCallback((folder: ExplorerFolder) => {
    const pinned = toggleFavorite(folder.key);
    setToast(pinned
      ? `“${folder.name}” fixada no acesso rápido.`
      : `“${folder.name}” removida do acesso rápido.`);
  }, [toggleFavorite]);

  const notify = useCallback((message: string) => setToast(message), []);
  const clearToast = useCallback(() => setToast(''), []);

  const download = useFileViewerDownload({ refresh, notify });
  const transfers = useFileViewerTransfers({ refresh, notify });

  const isAtRoot = path.length === 0;

  return {
    /** Usuário logado (`selectUserId`), para o seletor de empresa e os favoritos. */
    userId,
    /** Admin com a flag: a tela mostra o seletor de empresa. */
    canChooseCompany,
    /** Escopo em vigor (`null` enquanto o admin não escolheu). */
    company,
    setCompany,
    /** Admin sem empresa escolhida: a tela pede a escolha e a lista não é pedida. */
    needsCompany,
    isLoading,
    hasData: !!data,
    isRefreshing,
    refresh,
    /** Admin: Atualizar pede ao backend para reler o disco; cliente só renova a leitura do cache */
    canForceRefresh: isAdmin,
    updatedAt,
    root,
    currentFolder: currentFolder ?? root,
    path: effectivePath,
    goTo,
    goUp,
    canManage: isAdmin && !isAtRoot && !!currentFolder,
    query,
    setQuery: changeQuery,
    scope,
    setScope,
    kind: activeKind,
    setKind,
    sortKey: sort.key,
    sortDir: sort.dir,
    sortBy,
    setSortKey,
    visible,
    shownEntries: visible.entries.slice(0, limit),
    hiddenCount: Math.max(0, visible.entries.length - limit),
    showMore: () => setLimit((current) => current + PAGE_SIZE),
    selectedKeys: selected,
    selectedFiles,
    allVisibleSelected,
    someVisibleSelected,
    hasSelectableFiles: visibleFiles.length > 0,
    toggleSelected,
    toggleAllVisible,
    clearSelection,
    expanded,
    toggleExpanded,
    favoriteFolders,
    showQuickAccess: isAtRoot && visible.terms.length === 0,
    isFavorite,
    togglePin,
    toast,
    clearToast,
    download,
    transfers,
  };
}

export type FileExplorerState = ReturnType<typeof useFileExplorer>;
